import { inject, Injectable, signal } from '@angular/core';
import { UiCallFlow } from './teams-auto-attendant-edit-form/call-flows/call-flows-side-nav/call-flows-side-nav.component';
import {
  AutoAttendantButton,
  AutoAttendantButtonAction,
  AutoAttendantButtonAudioInfo,
  AutoAttendantButtonView,
  AutoAttendantHoliday,
  AutoAttendantOperatorType,
  CallFlow,
  CallFlowView,
  CallRoutingOption,
  DialKeys,
  DialKeysView,
  DirectorySearchMethod,
  MicrosoftTeamsAutoAttendant,
  MicrosoftTeamsAutoAttendantView,
  Prompt,
  RedirectCallSettings,
  RedirectCallSettingsType,
  RedirectCallSettingsView,
  VoiceAppViewEntityType,
  WeeklySchedule,
} from '../shared/models/generated/ziroModels';
import { ZiroSelectOption } from '../forms/fields/select/ziro-select.component';
import { cloneDeep } from 'lodash';
import { TeamsAutoAttendantsService } from './teams-auto-attendants.service';
import { CallFlowGreetingsOptions } from './teams-auto-attendant-edit-form/call-flows/call-flow-greetings/call-flow-greetings-form.component';
import { UiDialButton } from './teams-auto-attendant-edit-form/call-flows/auto-attendants-menu-buttons/teams-auto-attendants-menu-buttons.component';

@Injectable()
export class TeamsAutoAttendantEditContext {
  private _teamsAutoAttendantService = inject(TeamsAutoAttendantsService);

  readonly autoAttendant = signal<MicrosoftTeamsAutoAttendant | null>(null);
  readonly standardCallFlows = signal<UiCallFlow[]>([]);
  readonly localHolidayCallFlows = signal<UiCallFlow[]>([]);
  readonly holidays = signal<AutoAttendantHoliday[]>([]);

  private _buttonsByFlow = new Map<string, UiDialButton[]>();

  initExisting(
    view: MicrosoftTeamsAutoAttendantView,
    standardCallFlows: UiCallFlow[],
    holidayCallFlows: UiCallFlow[],
    holidays: AutoAttendantHoliday[],
  ): void {
    this.standardCallFlows.set(standardCallFlows);
    this.localHolidayCallFlows.set(holidayCallFlows);
    this.holidays.set(holidays);

    this.autoAttendant.set({
      id: view.id,
      name: view.name,
      language: view.language,
      timeZone: view.timeZone,
      teamsPhoneAgentEnabled: false,
      operator: view.operator
        ? {
            configurationEndpointType: '',
            destination: this._teamsAutoAttendantService.formatNumberToApi(view.operator.destination),
            type: view.operator.type,
          }
        : null,
      authorizedUserGuids: view.users.map((u) => u.guid),
      resourceAccountGuids: view.resourceAccounts.map((r) => r.guid),
      dialScopeInclusionGroups: view.dialScopeInclusionGroups.map((g) => g.guid),
      dialScopeExclusionGroups: view.dialScopeExclusionGroups.map((g) => g.guid),
      regularHours: view.businessHours,
      businessHoursCallFlow: view.businessHoursCallFlow ? this._toCallFlow(view.businessHoursCallFlow) : null,
      afterHoursCallFlow: view.afterHoursCallFlow ? this._toCallFlow(view.afterHoursCallFlow) : null,
      holidayCallFlows: (view.holidayCallFlows ?? []).map((hcf) => ({
        schedule: hcf.schedule,
        callFlow: this._toCallFlow(hcf.callFlow),
      })),
    });
  }

  initNew(holidays: AutoAttendantHoliday[]): void {
    const businessHoursFlowId = crypto.randomUUID();
    const afterHoursFlowId = crypto.randomUUID();

    const defaultCallFlow = (name: string): CallFlow => ({
      name,
      callRoutingOption: CallRoutingOption.DISCONNECT,
      greetingOption: null,
      playMenuOptionSettings: null,
      redirectCallSettings: null,
    });

    const defaultSchedule: WeeklySchedule = null;

    const businessHoursUiFlow: UiCallFlow = {
      id: businessHoursFlowId,
      ...defaultCallFlow('Business Hours'),
      schedule: defaultSchedule,
    };

    const afterHoursUiFlow: UiCallFlow = {
      id: afterHoursFlowId,
      ...defaultCallFlow('After Hours'),
      isAfterHoursCallFlow: true,
      schedule: defaultSchedule,
    };

    this.standardCallFlows.set([businessHoursUiFlow, afterHoursUiFlow]);
    this.localHolidayCallFlows.set([]);
    this.holidays.set(holidays);

    this.autoAttendant.set({
      id: null,
      name: '',
      language: '',
      teamsPhoneAgentEnabled: false,
      timeZone: '',
      operator: null,
      authorizedUserGuids: [],
      resourceAccountGuids: [],
      dialScopeInclusionGroups: [],
      dialScopeExclusionGroups: [],
      regularHours: null,
      businessHoursCallFlow: defaultCallFlow('Business Hours'),
      afterHoursCallFlow: null,
      holidayCallFlows: [],
    });
  }

  updateGeneralSettings(
    patch: Partial<Pick<MicrosoftTeamsAutoAttendant, 'name' | 'language' | 'timeZone' | 'operator'>>,
  ): void {
    const update = cloneDeep(patch);
    if (update.operator) {
      update.operator.type = this._toApiOperatorType(update.operator.type);
      if (update.operator.type === AutoAttendantOperatorType.EXTERNAL_PHONE_NUMBER) {
        update.operator.destination = this._teamsAutoAttendantService.formatNumberToApi(update.operator.destination);
      }
    }
    this.autoAttendant.update((teamsAutoAttendant) =>
      teamsAutoAttendant ? { ...teamsAutoAttendant, ...update } : teamsAutoAttendant,
    );
  }

  updateAuthorizedUserGuids(guids: string[]): void {
    this.autoAttendant.update((aa) => (aa ? { ...aa, authorizedUserGuids: guids } : aa));
  }

  updateResourceAccountGuids(resourceAccounts: ZiroSelectOption[]): void {
    this.autoAttendant.update((aa) =>
      aa ? { ...aa, resourceAccountGuids: resourceAccounts.map((ra) => ra.value) } : aa,
    );
  }

  updateDialScope(inclusionGroups: string[], exclusionGroups: string[]): void {
    this.autoAttendant.update((aa) =>
      aa
        ? {
            ...aa,
            dialScopeInclusionGroups: inclusionGroups,
            dialScopeExclusionGroups: exclusionGroups,
          }
        : aa,
    );
  }

  updateGreeting(
    flowId: string,
    greeting: Prompt | null,
    selectedGreetingOption?: CallFlowGreetingsOptions,
    isDestinationGreeting = false,
  ): void {
    this._patchCallFlow(flowId, (cf) => ({
      ...cf,
      greetingOption: isDestinationGreeting ? cf.greetingOption : greeting,
      playMenuOptionSettings: isDestinationGreeting
        ? {
            ...cf.playMenuOptionSettings,
            prompt: {
              audio: selectedGreetingOption === CallFlowGreetingsOptions.CUSTOM_GREETING ? greeting.audio : null,
              ttsMessage: selectedGreetingOption === CallFlowGreetingsOptions.TTS_MESSAGE ? greeting.ttsMessage : null,
            },
          }
        : { ...cf.playMenuOptionSettings, prompt: cf.playMenuOptionSettings?.prompt ?? null },
    }));

    this._updateUiFlow(flowId, (callFlow) => ({
      ...callFlow,
      greetingOption: isDestinationGreeting ? callFlow.greetingOption : greeting,
      selectedGreetingOption: isDestinationGreeting ? callFlow.selectedGreetingOption : selectedGreetingOption,
      selectedDestinationGreetingOption: isDestinationGreeting
        ? selectedGreetingOption
        : callFlow.selectedDestinationGreetingOption,
      playMenuOptionSettings: {
        ...callFlow.playMenuOptionSettings,
        prompt: isDestinationGreeting
          ? {
              audio: selectedGreetingOption === CallFlowGreetingsOptions.CUSTOM_GREETING ? greeting.audio : null,
              ttsMessage: selectedGreetingOption === CallFlowGreetingsOptions.TTS_MESSAGE ? greeting.ttsMessage : null,
            }
          : callFlow.playMenuOptionSettings?.prompt,
      },
    }));
  }

  updateSchedule(flowId: string, schedule: WeeklySchedule): void {
    const standard = this.standardCallFlows().find((f) => f.id === flowId);
    if (standard) {
      const afterHoursUiFlow = this.standardCallFlows().find((f) => f.isAfterHoursCallFlow);

      this.autoAttendant.update((aa) => {
        if (!aa) {
          return aa;
        }
        return {
          ...aa,
          regularHours: schedule,
          afterHoursCallFlow:
            aa.afterHoursCallFlow ?? (afterHoursUiFlow ? this._toCallFlowFromUi(afterHoursUiFlow) : null),
        };
      });
    }

    this._updateUiFlow(flowId, (f) => ({ ...f, schedule }));
  }

  updateHolidaySchedule(flowId: string, holiday: AutoAttendantHoliday): void {
    const uiFlow = this.localHolidayCallFlows().find((f) => f.id === flowId);
    const currentScheduleId = uiFlow?.holiday?.id;

    this._updateUiFlow(flowId, (f) => ({ ...f, holiday }));
    this.autoAttendant.update((aa) => {
      if (!aa) {
        return aa;
      }
      return {
        ...aa,
        holidayCallFlows: aa.holidayCallFlows.map((hcf) =>
          hcf.schedule?.id === currentScheduleId ? { ...hcf, schedule: holiday } : hcf,
        ),
      };
    });
  }

  addHolidayCallFlow(uiFlow: UiCallFlow): void {
    this.localHolidayCallFlows.update((flows) => [...flows, uiFlow]);
  }

  removeHolidayCallFlow(flowId: string): void {
    const flow = this.localHolidayCallFlows().find((f) => f.id === flowId);
    const updated = this.localHolidayCallFlows().filter((f) => f.id !== flowId);
    this.localHolidayCallFlows.set(updated);

    if (flow?.holiday?.id) {
      this.autoAttendant.update((aa) =>
        aa
          ? {
              ...aa,
              holidayCallFlows: aa.holidayCallFlows.filter((hcf) => hcf.schedule?.id !== flow.holiday.id),
            }
          : aa,
      );
    }
  }

  updateHolidayCallFlowName(flowId: string, name: string): void {
    this._updateUiFlow(flowId, (f) => ({ ...f, name }));
    this._patchCallFlow(flowId, (cf) => ({ ...cf, name }));
  }

  updateDialKeys(flowId: string, buttons: UiDialButton[]): void {
    this._buttonsByFlow.set(flowId, buttons);
    const dialKeys = this._buildDialKeys(buttons);
    const applyDialKeys = (settings: CallFlow['playMenuOptionSettings']): CallFlow['playMenuOptionSettings'] =>
      settings
        ? { ...settings, dialKeys }
        : { forceListen: false, directorySearchMethod: DirectorySearchMethod.NONE, prompt: null, dialKeys };

    this._patchCallFlow(flowId, (cf) => ({ ...cf, playMenuOptionSettings: applyDialKeys(cf.playMenuOptionSettings) }));
    this._updateUiFlow(flowId, (f) => ({ ...f, playMenuOptionSettings: applyDialKeys(f.playMenuOptionSettings) }));
  }

  updateCallRoutingSettings(
    flowId: string,
    callRoutingOption: CallRoutingOption,
    redirectCallSettings: RedirectCallSettingsView,
    directorySearchMethod: DirectorySearchMethod,
  ): void {
    const patch = (cf: CallFlow): CallFlow => {
      if (callRoutingOption === CallRoutingOption.REDIRECT_CALL) {
        const callSettings = {
          type: redirectCallSettings?.type || cf.redirectCallSettings?.type,
          destination: this._getRedirectCallSettingsDestination(redirectCallSettings, cf),
        };

        return {
          ...cf,
          callRoutingOption,
          redirectCallSettings: callSettings,
        };
      }
      if (callRoutingOption === CallRoutingOption.PLAY_MENU_OPTIONS) {
        const existing = cf.playMenuOptionSettings;
        return {
          ...cf,
          callRoutingOption,
          playMenuOptionSettings: {
            forceListen: existing?.forceListen ?? false,
            prompt: existing?.prompt ?? null,
            dialKeys: existing?.dialKeys ?? null,
            directorySearchMethod,
          },
        };
      }
      return { ...cf, callRoutingOption };
    };

    this._patchCallFlow(flowId, patch);
    this._updateUiFlow(flowId, (f) => ({ ...f, ...patch(f) }));
  }

  private _getRedirectCallSettingsDestination(redirectCallSettings: RedirectCallSettingsView, cf: CallFlow): string {
    return redirectCallSettings?.type === RedirectCallSettingsType.EXTERNAL_PHONE_NUMBER
      ? redirectCallSettings.destination
      : redirectCallSettings?.type === RedirectCallSettingsType.VOICEMAIL
        ? redirectCallSettings?.destination
        : (redirectCallSettings?.entity?.guid ?? cf.redirectCallSettings?.destination ?? '');
  }

  getButtonsForFlow(flowId: string): UiDialButton[] {
    return this._buttonsByFlow.get(flowId) ?? [];
  }

  getFlowState(flowId: string): {
    greetingOption: Prompt | null;
    schedule: WeeklySchedule | null;
    pendingGreetingOption?: CallFlowGreetingsOptions;
    pendingDestinationGreetingOption?: CallFlowGreetingsOptions;
  } | null {
    const ui = [...this.standardCallFlows(), ...this.localHolidayCallFlows()].find((f) => f.id === flowId);
    if (!ui) {
      return null;
    }
    return {
      greetingOption: ui.greetingOption ?? null,
      schedule: ui.schedule ?? null,
      pendingGreetingOption: ui.selectedGreetingOption,
      pendingDestinationGreetingOption: ui.selectedDestinationGreetingOption,
    };
  }

  patchDialKeyAudioInfo(
    flowId: string,
    prop: keyof DialKeys,
    audioInfo: AutoAttendantButtonAudioInfo,
    audioId: string,
  ): void {
    this._patchCallFlow(flowId, (callFlow) => {
      const currentDialKeys = callFlow.playMenuOptionSettings?.dialKeys;
      if (!currentDialKeys) {
        return callFlow;
      }

      const existingButton = currentDialKeys[prop];
      if (!existingButton) {
        return callFlow;
      }

      const dialKeys: DialKeys = {
        ...currentDialKeys,
        [prop]: {
          ...existingButton,
          audioInfo,
          destination:
            existingButton.action === AutoAttendantButtonAction.ANNOUNCEMENT_AUDIO_FILE
              ? audioId
              : existingButton.destination,
        },
      };

      return {
        ...callFlow,
        playMenuOptionSettings: {
          ...callFlow.playMenuOptionSettings,
          dialKeys,
        },
      };
    });
  }

  removeEntityFromObject<T>(value: T): T {
    if (Array.isArray(value)) {
      return value.map((item) => this.removeEntityFromObject(item)) as T;
    }
    if (!value || typeof value !== 'object') {
      return value;
    }

    const cleanedValue = { ...(value as Record<string, unknown>) };
    delete cleanedValue['entity'];
    return Object.entries(cleanedValue).reduce(
      (acc, [key, nestedValue]) => ({
        ...acc,
        [key]: this.removeEntityFromObject(nestedValue),
      }),
      {} as T,
    );
  }

  private _toCallFlow(cf: CallFlowView): CallFlow {
    return {
      name: cf.name ?? '',
      callRoutingOption: cf.callRoutingOption ?? null,
      greetingOption: cf.greetingOption ?? null,
      playMenuOptionSettings: cf.playMenuOptionSettings
        ? {
            forceListen: cf.playMenuOptionSettings.forceListen,
            directorySearchMethod: cf.playMenuOptionSettings.directorySearchMethod,
            prompt: cf.playMenuOptionSettings.prompt,
            dialKeys: cf.playMenuOptionSettings.dialKeys ? this._toDialKeys(cf.playMenuOptionSettings.dialKeys) : null,
          }
        : null,
      redirectCallSettings: cf.redirectCallSettings ? this._toRedirectCallSettings(cf.redirectCallSettings) : null,
    };
  }

  private _toDialKeys(view: DialKeysView): DialKeys {
    const mapButton = (btn: AutoAttendantButtonView | null | undefined): AutoAttendantButton | null => {
      if (!btn) {
        return null;
      }
      return {
        action: btn.action,
        audioInfo: btn.audioInfo,
        destination: btn.destination,
        voiceCommands: btn.voiceCommands,
      };
    };
    return {
      button0: mapButton(view.button0),
      button1: mapButton(view.button1),
      button2: mapButton(view.button2),
      button3: mapButton(view.button3),
      button4: mapButton(view.button4),
      button5: mapButton(view.button5),
      button6: mapButton(view.button6),
      button7: mapButton(view.button7),
      button8: mapButton(view.button8),
      button9: mapButton(view.button9),
      buttonPound: mapButton(view.buttonPound),
      buttonStar: mapButton(view.buttonStar),
    };
  }

  private _toRedirectCallSettings(view: RedirectCallSettingsView): RedirectCallSettings {
    return {
      destination: view.destination,
      type: view.type,
    };
  }

  private _patchCallFlow(flowId: string, patcher: (cf: CallFlow) => CallFlow): void {
    const standard = this.standardCallFlows().find((f) => f.id === flowId);
    const isAfterHours = standard?.isAfterHoursCallFlow;

    this.autoAttendant.update((aa) => {
      if (!aa) {
        return aa;
      }

      if (standard) {
        if (isAfterHours) {
          const currentAfterHoursCallFlow = aa.afterHoursCallFlow ?? this._toCallFlowFromUi(standard);
          return {
            ...aa,
            afterHoursCallFlow: patcher(currentAfterHoursCallFlow),
          };
        }
        return {
          ...aa,
          businessHoursCallFlow: patcher(aa.businessHoursCallFlow),
        };
      }

      const uiHoliday = this.localHolidayCallFlows().find((f) => f.id === flowId);
      if (uiHoliday) {
        return {
          ...aa,
          holidayCallFlows: aa.holidayCallFlows.map((hcf) => {
            const matchById = uiHoliday.holiday?.id && hcf.schedule?.id === uiHoliday.holiday.id;
            const matchByFlowId = !uiHoliday.holiday?.id && hcf.callFlow?.name === uiHoliday.name;
            return matchById || matchByFlowId ? { ...hcf, callFlow: patcher(hcf.callFlow) } : hcf;
          }),
        };
      }

      return aa;
    });
  }

  private _updateUiFlow(flowId: string, updater: (f: UiCallFlow) => UiCallFlow): void {
    this.standardCallFlows.update((flows) => flows.map((flow) => (flow.id === flowId ? updater(flow) : flow)));
    this.localHolidayCallFlows.update((flows) => flows.map((flow) => (flow.id === flowId ? updater(flow) : flow)));
  }

  private _toApiOperatorType(type: AutoAttendantOperatorType | string): AutoAttendantOperatorType {
    switch (type as string) {
      case VoiceAppViewEntityType.RESOURCE_ACCOUNT:
        return AutoAttendantOperatorType.APPLICATION_ENDPOINT;
      case VoiceAppViewEntityType.AUTO_ATTENDANT:
      case VoiceAppViewEntityType.CALL_QUEUE:
      case VoiceAppViewEntityType.VOICE_APP:
        return AutoAttendantOperatorType.CONFIGURATION_ENDPOINT;
      default:
        return type as AutoAttendantOperatorType;
    }
  }

  private _toCallFlowFromUi(uiFlow: UiCallFlow): CallFlow {
    return {
      name: uiFlow.name,
      callRoutingOption: uiFlow.callRoutingOption ?? CallRoutingOption.DISCONNECT,
      greetingOption: uiFlow.greetingOption ?? null,
      playMenuOptionSettings: uiFlow.playMenuOptionSettings ?? null,
      redirectCallSettings: uiFlow.redirectCallSettings ?? null,
    };
  }

  private _buildDialKeys(buttons: UiDialButton[]): DialKeys {
    const dialKeys: DialKeys = {
      button0: null,
      button1: null,
      button2: null,
      button3: null,
      button4: null,
      button5: null,
      button6: null,
      button7: null,
      button8: null,
      button9: null,
      buttonPound: null,
      buttonStar: null,
    };

    for (const btn of buttons) {
      if (!btn.action || btn.action === AutoAttendantButtonAction.NOT_SUPPORTED) {
        continue;
      }
      dialKeys[btn.prop as keyof DialKeys] = {
        action: btn.action,
        destination: this._resolveButtonDestination(btn),
        audioInfo: this._resolveButtonAudioInfo(btn),
        voiceCommands: btn.voiceCommands ?? [],
      };
    }

    return dialKeys;
  }

  private _resolveButtonDestination(btn: UiDialButton): string {
    if (
      btn.action === AutoAttendantButtonAction.ANNOUNCEMENT_AUDIO_FILE ||
      btn.action === AutoAttendantButtonAction.EXTERNAL_PHONE_NUMBER
    ) {
      return btn.destination ?? '';
    }
    return btn.entity?.guid ?? btn.destination ?? '';
  }

  private _resolveButtonAudioInfo(btn: UiDialButton): AutoAttendantButtonAudioInfo | null {
    if (btn.audioFile instanceof File) {
      return { fileName: btn.audioFile.name, downloadUri: null };
    }
    return btn.audioInfo ?? null;
  }
}
